import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, WifiIcon, WaypointsIcon, CameraIcon, UserIcon, BarChart3Icon, BellIcon, CreditCardIcon, MessageSquareTextIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
import { waterFinalCta as c, waterFinalCapabilities, type Bi, type WaterCapabilityKey } from '../../data/waterFinalCta';
const EASE = [0.23, 1, 0.32, 1] as const;
const ICONS: Record<WaterCapabilityKey, typeof BellIcon> = {
  sensors: WifiIcon,
  api: WaypointsIcon,
  photo: CameraIcon,
  identity: UserIcon,
  insights: BarChart3Icon,
  notifications: BellIcon,
  requests: MessageSquareTextIcon,
  payments: CreditCardIcon
};
// Very soft ambient light toward the outer edges, as in the reference.
const AMBIENT = 'radial-gradient(ellipse 30% 45% at 0% 35%, rgba(79,70,229,0.07), transparent 70%), radial-gradient(ellipse 28% 40% at 100% 70%, rgba(79,70,229,0.07), transparent 70%)';
export function WaterFinalCTA() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (b: Bi) => isFR ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-[#FCFCFE] border-t border-[#EEF0F4]" aria-labelledby="water-final-cta-title" data-id="element-7512" data-mp="3fpyjsex4bbdq">
      <div className="absolute inset-0 pointer-events-none" style={{
      background: AMBIENT
    }} aria-hidden="true" data-id="element-7513" data-mp="3aqc3fwlztf0p" />

      <motion.div initial={{
      opacity: 0,
      y: 8
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} className="relative z-10 max-w-[1280px] mx-auto px-6 pt-28 md:pt-36 pb-32 md:pb-40 text-center" data-id="element-7514" data-mp="35tlml72d6fe8">
        <p className="font-sans text-[13px] md:text-[14px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-7515" data-mp="2ryjag26xbtsz">{pick(c.eyebrow)}</p>

        <h2 id="water-final-cta-title" className="font-serif text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] xl:text-[5.4rem] leading-[1.02] tracking-tight mt-9" data-id="element-7516" data-mp="2i2q3cybz4sil">
          <span className="block text-[#0E1330]" data-id="element-7517" data-mp="28gx6l6qc7vg1">{pick(c.navy)}</span>
          <span className="block text-[#4F46E5]" data-id="element-7518" data-mp="2deqblwfwmjeq">{pick(c.indigo)}</span>
        </h2>

        <p className="font-sans text-[16px] md:text-[19px] leading-[1.6] text-[#5B6690] mt-9 max-w-[1000px] mx-auto" data-id="element-7519" data-mp="1zexkw14do36r">{pick(c.body)}</p>

        <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4" data-id="element-7520" data-mp="23g12as22q7a3">
          <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
          location: 'Smart Water Final'
        })} className="inline-flex items-center justify-center gap-2.5 h-[60px] px-10 rounded-lg bg-[#4F46E5] font-sans text-[16px] font-medium text-white whitespace-nowrap shadow-[0_14px_30px_-16px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-7521" data-mp="2r7ev0z4504vl">
            {pick(c.primary)}
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-7522" data-mp="3ev65dx64a7u8" />
          </Link>
          <Link to="/request-demo" onClick={() => track('Contact CTA Clicked', {
          location: 'Smart Water Final'
        })} className="inline-flex items-center justify-center h-[60px] px-10 rounded-lg border border-[#D9DBEE] bg-white font-sans text-[16px] font-medium text-[#0E1330] whitespace-nowrap hover:border-[#4F46E5]/50 transition-colors duration-150" data-id="element-7523" data-mp="13fffvvf1ungv">
            {pick(c.secondary)}
          </Link>
        </div>

        <span className="block w-16 h-[2px] bg-[#4F46E5] mx-auto mt-11" aria-hidden="true" data-id="element-7524" data-mp="kj5vkqdk10of" />

        <ul className="mt-10 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-y-7 xl:gap-y-0 xl:divide-x xl:divide-[#E4E6F0]" data-id="element-7525" data-mp="fjtb2ca5o5p0">
          {waterFinalCapabilities.map(cap => {
          const Icon = ICONS[cap.key];
          return <li key={cap.key} className="flex flex-col items-center gap-2.5 xl:px-2" data-id="element-7526" data-mp="25twh146z4e4h">
                <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-7527" data-mp="20vl76tgoi3m9" />
                <span className="font-sans text-[14px] text-[#5B6690] whitespace-nowrap" data-id="element-7528" data-mp="aehay0atl0qu">{pick(cap.label)}</span>
              </li>;
        })}
        </ul>
      </motion.div>

      <svg className="absolute bottom-0 left-0 w-full h-[110px] pointer-events-none" viewBox="0 0 1440 110" preserveAspectRatio="none" aria-hidden="true" data-id="element-7529" data-mp="ff59r85ailvm">
        <path d="M0 30 C 360 100, 1080 100, 1440 0 L1440 110 L0 110 Z" fill="#FFFFFF" data-id="element-7530" data-mp="oes274g1khcj" />
      </svg>
    </section>;
}